import { useUser } from "@/context/useUser";
import { getTenantId } from "@/lib/storeManager";
import LoginFormSchema from "@/schemas/LoginFromSchema";
import SocialConfigurationService from "@/services/APIs/SocialConfigurationService";
import navigationRoutes from "@/utils/navigationRoutes";
import { getApiErrorMessage } from "@project/lib/api-error";
import { notify } from "@/components/common/Notification";
import { FormItem, Input, Password, Submit } from "@formily/antd-v5";
import { createForm } from "@formily/core";
import { FormProvider, createSchemaField } from "@formily/react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const form = createForm({});
const loginBackgroundPath = "/assets/login-bg.avif";
const loginLogoPath = "/assets/jivandhara-logo.png";

const SchemaField = createSchemaField({
  components: { FormItem, Input, Password },
});

const LoginForm: React.FC = () => {
  const { login, isAuthenticated, socialLogin } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const tenantId = getTenantId() ?? "";

  const handleSubmit = async (values: { email: string; password: string }) => {
    setLoading(true);
    try {
      const response = await login(values.email, values.password);
      if (response) {
        navigate(navigationRoutes.SCHEMAS);
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Invalid email or password"));
    } finally {
      setLoading(false);
    }
  };

  const { data } = useQuery({
    queryKey: ["socialConfigurations", tenantId],
    queryFn: () => SocialConfigurationService.listSocialConfigurations(1, 10),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  // console.log(data);

  const providerStyles: Record<string, string> = {
    google: "bg-blue-500",
    github: "bg-gray-800",
    apple: "bg-black",
    facebook: "bg-blue-700",
    linkedin: "bg-blue-800",
    twitter: "bg-blue-900",
    instagram: "bg-black",
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate(navigationRoutes.SCHEMAS);
    }
  }, [isAuthenticated, tenantId, navigate]);

  const handleOAuthLogin = async (provider: string) => {
    try {
      const response = await socialLogin(provider);
      if (response?.auth_url) {
        window.location.href = response.auth_url;
      } else {
        console.error("OAuth login failed: no auth_url returned", response);
      }
    } catch (err) {
      console.error("Error during OAuth login", err);
    }
  };

  return (
    <div
      className="fixed inset-0 z-0 flex min-h-screen w-screen items-center justify-center overflow-hidden p-4"
      style={{
        backgroundImage: `url("${loginBackgroundPath}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
      }}
    >
      <div className="relative z-10 w-full max-w-md">
        <Card className="auth-card rounded-xl border border-slate-200/80 shadow-xl shadow-slate-900/10">
          <div className="mb-6 text-center">
            <img
              src={loginLogoPath}
              alt="Jivandhara Old Age Home Logo"
              className="mx-auto mb-3 h-20 w-auto object-contain"
            />
            <h1 className="text-2xl font-bold text-slate-800">
              Jivandhara Old Age Home
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Sign in to your admin panel
            </p>
          </div>

          <FormProvider form={form}>
            <SchemaField schema={LoginFormSchema} />
            <Submit
              onSubmit={handleSubmit}
              disabled={loading}
              className="w-full rounded-md !bg-blue-600 px-4 py-2 font-medium text-white hover:!bg-blue-700"
            >
              {loading ? "Logging in..." : "Login"}
            </Submit>
          </FormProvider>

          <div className="mt-6 space-y-2">
            {data?.data?.map((config: any) => {
              const provider = config.provider.toLowerCase();
              const style = providerStyles[provider] ?? "bg-gray-500";
              const label =
                provider.charAt(0).toUpperCase() + provider.slice(1);

              return (
                <button
                  key={config.id}
                  onClick={() => handleOAuthLogin(provider)}
                  className={`w-full rounded-md px-4 py-2 text-sm font-medium text-white ${style}`}
                >
                  Login with {label}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex flex-col items-center gap-1.5 text-center">
            <span className="text-sm text-slate-500">
              {"Don't have an account?"}
            </span>
            <Link
              to={navigationRoutes.SIGNUP}
              className="text-sm font-medium text-blue-600"
            >
              Signup
            </Link>
            <Link
              to={navigationRoutes.FORGOT_PASSWORD}
              className="text-sm font-medium text-blue-600"
            >
              Forgot Password?
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default LoginForm;
